import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowUpRight01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  ColorPickerIcon,
  Copy01Icon,
  Download01Icon,
  ExternalLinkIcon,
  FileImageIcon,
  GridViewIcon,
  InformationCircleIcon,
  Layout01Icon,
  Moon02Icon,
  PaintBoardIcon,
  SecurityCheckIcon,
  SparklesIcon,
  Sun03Icon,
  TextFontIcon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ViaBrandLockup, PrismaHexagon, PrismaViaWordmark } from '@/components/brand/InstitutionalBrand';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

const BRAND_ROOT = '/assets/brand';

const LOGOS = [
  {
    source: 'logo_uc_color.svg',
    title: 'Logo original color',
    description: 'Versión oficial a color (SVG). Para fondos blancos y claros.',
    surface: 'bg-white',
  },
  {
    source: 'logo_dark_2x.png',
    title: 'Logo principal',
    description: 'Para fondos blancos y superficies claras.',
    surface: 'bg-white',
  },
  {
    source: 'logo_white_2x.png',
    title: 'Logo invertido',
    description: 'Para fondos púrpura, azul u oscuros.',
    surface: 'bg-primary',
  },
  {
    source: 'logo_badge.png',
    title: 'Badge institucional',
    description: 'Para piezas de campaña y Estrategia 2027.',
    surface: 'bg-secondary/10',
  },
];

const COLOR_GROUPS = {
  brand: [
    { hex: '#FBF7FC', name: 'Púrpura 50', usage: 'Fondos sutiles' },
    { hex: '#F3E7F5', name: 'Púrpura 100', usage: 'Estados seleccionados' },
    { hex: '#D1A5D7', name: 'Púrpura 300', usage: 'Bordes destacados' },
    { hex: '#9B4CA6', name: 'Púrpura 500', usage: 'Gráficos y apoyo' },
    { hex: '#671E75', name: 'Púrpura 700', usage: 'Primario oficial · CTA y navegación' },
    { hex: '#3D1046', name: 'Púrpura 950', usage: 'Fondos profundos' },
    { hex: '#F4F8FE', name: 'Azul 50', usage: 'Fondos informativos' },
    { hex: '#CFE1F9', name: 'Azul 200', usage: 'Bordes y ayudas' },
    { hex: '#76AAED', name: 'Azul 400', usage: 'Visualizaciones' },
    { hex: '#307FE2', name: 'Azul 600', usage: 'Secundario oficial · enlaces e información' },
    { hex: '#1E5298', name: 'Azul 800', usage: 'Énfasis sobre fondos claros' },
    { hex: '#193F73', name: 'Azul 900', usage: 'Fondos azules' },
  ],
  neutral: [
    { hex: '#FFFFFF', name: 'Blanco', usage: 'Superficies y tarjetas' },
    { hex: '#F8FAFC', name: 'Canvas', usage: 'Fondo claro' },
    { hex: '#EEF0F4', name: 'Border soft', usage: 'Separadores y fondos de control' },
    { hex: '#D9DEE7', name: 'Border', usage: 'Bordes visibles' },
    { hex: '#94A3B8', name: 'Subtle', usage: 'Deshabilitados y placeholders' },
    { hex: '#64748B', name: 'Muted', usage: 'Texto secundario' },
    { hex: '#4B5563', name: 'Body', usage: 'Texto de cuerpo' },
    { hex: '#1E2330', name: 'Text', usage: 'Títulos y texto principal' },
    { hex: '#0F172A', name: 'Ink', usage: 'Alto contraste' },
    { hex: '#0D0F17', name: 'Night', usage: 'Fondo oscuro' },
  ],
  support: [
    { hex: '#1F4E79', name: 'Azul institucional', usage: 'Materiales heredados y documentos' },
    { hex: '#2E75B6', name: 'Azul secundario', usage: 'Gráficos y documentos' },
    { hex: '#5823C9', name: 'Violeta digital', usage: 'Acentos de producto' },
    { hex: '#6B50FF', name: 'Violeta claro', usage: 'Gradientes y hover' },
    { hex: '#00A6A6', name: 'Turquesa clínico', usage: 'Salud, datos y monitoreo' },
    { hex: '#7C3AED', name: 'IA', usage: 'Automatización e inteligencia artificial' },
  ],
  semantic: [
    { hex: '#10B981', name: 'Éxito', usage: 'Aprobado, saludable, completado' },
    { hex: '#F59E0B', name: 'Advertencia', usage: 'Atención, vencimiento, pendiente' },
    { hex: '#EF4444', name: 'Error', usage: 'Crítico, suspensión y error' },
    { hex: '#2563EB', name: 'Información', usage: 'Orientación, ayuda y enlaces' },
    { hex: '#E11D48', name: 'Riesgo', usage: 'Riesgo alto y escalamiento' },
    { hex: '#64748B', name: 'Neutral', usage: 'Estados sin clasificación' },
  ],
};

const THEMES = [
  {
    id: 'light',
    name: 'Claro ejecutivo',
    description: 'Lectura prolongada, gestión y trabajo operativo.',
    icon: Sun03Icon,
    colors: {
      background: '#F8FAFC',
      foreground: '#1E2330',
      card: '#FFFFFF',
      primary: '#671E75',
      secondary: '#307FE2',
      muted: '#64748B',
      border: '#EEF0F4',
    },
  },
  {
    id: 'dark',
    name: 'Oscuro ejecutivo',
    description: 'Monitoreo, tableros y espacios de baja luminosidad.',
    icon: Moon02Icon,
    colors: {
      background: '#0D0F17',
      foreground: '#F1F5F9',
      card: '#151924',
      primary: '#9B4CA6',
      secondary: '#76AAED',
      muted: '#94A3B8',
      border: '#2B3242',
    },
  },
  {
    id: 'purple',
    name: 'UC Purple',
    description: 'Experiencias institucionales, campañas y momentos VIA.',
    icon: SparklesIcon,
    colors: {
      background: '#FBF7FC',
      foreground: '#1E2330',
      card: '#FFFFFF',
      primary: '#671E75',
      secondary: '#307FE2',
      muted: '#64748B',
      border: '#E8DCEA',
    },
  },
];

const CORRECT_USE = [
  'Utilizar únicamente los archivos oficiales publicados en VIA.',
  'Mantener un área de protección equivalente al alto de la U del logotipo.',
  'Conservar siempre la proporción original y suficiente contraste.',
  'Usar #671E75 como primario y #307FE2 como secundario.',
  'Aplicar Inter en toda la interfaz digital de VIA.',
  'Usar Hugeicons Stroke Rounded como única familia de iconos.',
  'Incluir UC CHRISTUS en accesos, navegación y piezas institucionales.',
];

const INCORRECT_USE = [
  'Distorsionar, rotar, recortar o reconstruir el logotipo.',
  'Agregar sombras, contornos o efectos al wordmark.',
  'Invertir la jerarquía cromática definida para VIA.',
  'Usar el logo oscuro sobre fondos con contraste insuficiente.',
  'Crear nuevas variantes o colores no aprobados.',
  'Mezclar Hugeicons con Lucide, Tabler, Font Awesome u otras familias.',
  'Usar banners institucionales como decoración sin propósito.',
];

const DOWNLOADS = [
  { file: 'prisma_hexagon.svg', label: 'Isotipo VIA', description: 'SVG · símbolo del producto' },
  { file: 'logo_dark_2x.png', label: 'Logo UC CHRISTUS', description: 'PNG · fondos claros' },
  { file: 'logo_white_2x.png', label: 'Logo invertido', description: 'PNG · fondos oscuros' },
  { file: 'logo_badge.png', label: 'Badge Estrategia 2027', description: 'PNG · campañas' },
  { file: 'favicon.ico', label: 'Favicon institucional', description: 'ICO · navegadores' },
  { file: 'icon-192.png', label: 'Icono de aplicación', description: 'PNG · 192 × 192' },
  { file: 'banner_linkedin.png', label: 'Banner corporativo', description: 'PNG · 1584 × 396' },
  { file: 'fondo_teams_1.png', label: 'Fondo institucional', description: 'PNG · 1920 × 1080' },
  { file: 'footer_somos.png', label: 'Firma “Somos”', description: 'PNG · footer y comunicaciones' },
  { file: 'brand-guidelines.json', label: 'Tokens de marca', description: 'JSON · fuente legible por máquinas' },
];

const EXTERNAL_RESOURCES = [
  {
    title: 'Galería oficial Hugeicons',
    description: 'Busca, previsualiza, copia y descarga los iconos aprobados para VIA.',
    href: 'https://hugeicons.com/icons',
    action: 'Explorar iconos',
    icon: GridViewIcon,
  },
  {
    title: 'Hugeicons para React',
    description: 'Documentación oficial para implementar el set Stroke Rounded.',
    href: 'https://hugeicons.com/docs/integrations/react/quick-start',
    action: 'Ver documentación',
    icon: Layout01Icon,
  },
  {
    title: 'Inter · sitio oficial',
    description: 'Especímenes, características y documentación de la tipografía de VIA.',
    href: 'https://rsms.me/inter/',
    action: 'Ver tipografía',
    icon: TextFontIcon,
  },
  {
    title: 'Descargar Inter 4.1',
    description: 'Paquete oficial con fuentes variables, estáticas y licencia SIL OFL.',
    href: 'https://github.com/rsms/inter/releases/download/v4.1/Inter-4.1.zip',
    action: 'Descargar fuente',
    icon: Download01Icon,
  },
];

export default function BrandCenter() {
  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-hero text-white" aria-labelledby="brand-title">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.12) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-10 px-6 py-16 md:flex-row md:items-end md:justify-between md:py-20">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-5 border-white/20 bg-white/10 text-white">
              <HugeiconsIcon icon={ColorPickerIcon} data-icon="inline-start" />
              Fuente oficial de identidad visual
            </Badge>
            <h1 id="brand-title" className="font-display text-4xl font-extrabold leading-tight sm:text-5xl">
              Identidad UC CHRISTUS para VIA
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Lineamientos, activos y patrones aprobados para crear experiencias de inteligencia artificial coherentes,
              accesibles y reconocibles en toda la organización.
            </p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur-sm">
            <ViaBrandLockup contrast="light" />
            <Separator className="my-5 bg-white/15" />
            <p className="max-w-xs text-xs leading-relaxed text-white/60">
              Versión VIA 1.1 · Inter · Hugeicons · Primario #671E75 · Secundario #307FE2
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto flex max-w-7xl flex-col gap-16 px-6 py-14 md:py-20">
        <MotionSection>
          <SectionHeading
            icon={SparklesIcon}
            eyebrow="00 · Marca del producto"
            title="VIA — isotipo y wordmark"
            description="El isotipo es el hexágono prisma de UC CHRISTUS. El rombo central es hueco, por lo que luce sobre fondos claros; sobre superficies oscuras o púrpura debe ir sobre una placa clara para conservar el rombo."
          />
          <div className="mb-6 grid gap-5 md:grid-cols-2">
            <Card className="overflow-hidden">
              <div className="flex h-44 items-center justify-center gap-8 bg-canvas p-8">
                <PrismaHexagon className="h-24" />
                <PrismaViaWordmark contrast="dark" className="text-3xl" />
              </div>
              <CardHeader>
                <CardTitle className="text-base">Sobre fondo claro</CardTitle>
                <CardDescription>Isotipo directo y wordmark VIA (azul).</CardDescription>
              </CardHeader>
            </Card>
            <Card className="overflow-hidden">
              <div
                className="flex h-44 items-center justify-center gap-8 p-8"
                style={{ background: 'linear-gradient(150deg,#3D1046,#671E75 55%,#285AAA)' }}>
                <PrismaHexagon className="h-24" />
                <PrismaViaWordmark contrast="light" className="text-3xl" />
              </div>
              <CardHeader>
                <CardTitle className="text-base">Sobre fondo oscuro o púrpura</CardTitle>
                <CardDescription>
                  El isotipo va directo (fondo transparente); el wordmark usa los tonos de alto contraste.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 rounded-xl border bg-card p-4">
              <ViaBrandLockup contrast="dark" />
            </div>
            <div
              className="flex items-center gap-3 rounded-xl p-4"
              style={{ background: 'linear-gradient(150deg,#3D1046,#671E75 60%,#285AAA)' }}>
              <ViaBrandLockup contrast="light" variant="institutional" />
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href={`${BRAND_ROOT}/prisma_hexagon.svg`} download>
                <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                Descargar isotipo (SVG)
              </a>
            </Button>
          </div>
          <Alert className="mb-14 border-secondary/30 bg-secondary/5">
            <HugeiconsIcon icon={InformationCircleIcon} />
            <AlertTitle>Regla de fondo del isotipo</AlertTitle>
            <AlertDescription>
              El rombo central es transparente. Nunca coloques el isotipo directamente sobre fondos oscuros o de bajo
              contraste sin una placa clara; de lo contrario el rombo desaparece y el símbolo pierde legibilidad.
            </AlertDescription>
          </Alert>

          <SectionHeading
            icon={FileImageIcon}
            eyebrow="01 · Identidad institucional"
            title="Logotipo UC CHRISTUS y variantes aprobadas"
            description="El wordmark UC CHRISTUS es el identificador institucional. Usa cada variante según el contraste de la superficie."
          />
          <StaggerContainer className="grid gap-5 md:grid-cols-3">
            {LOGOS.map((logo) => (
              <StaggerItem key={logo.source}>
                <Card className="h-full overflow-hidden transition-shadow hover:shadow-float">
                  <div className={cn('flex h-40 items-center justify-center p-8', logo.surface)}>
                    <img
                      src={`${BRAND_ROOT}/${logo.source}`}
                      alt={`${logo.title} UC CHRISTUS`}
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-base">{logo.title}</CardTitle>
                    <CardDescription>{logo.description}</CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`${BRAND_ROOT}/${logo.source}`} download>
                        <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                        Descargar
                      </a>
                    </Button>
                  </CardFooter>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
          <Alert className="mt-5 border-warning/30 bg-warning/5">
            <HugeiconsIcon icon={InformationCircleIcon} />
            <AlertTitle>Área de protección</AlertTitle>
            <AlertDescription>
              Mantén alrededor del logo un espacio libre mínimo equivalente al alto de la letra “U”. Nunca alteres su
              proporción, tipografía, color ni composición.
            </AlertDescription>
          </Alert>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={ColorPickerIcon}
            eyebrow="02 · Sistema cromático"
            title="Paleta completa, copiable y descargable"
            description="Copia un color individual o exporta cada grupo como JSON o CSS para productos, documentos y visualizaciones."
          />
          <Tabs defaultValue="brand">
            <TabsList className="h-auto flex-wrap">
              <TabsTrigger value="brand">Marca</TabsTrigger>
              <TabsTrigger value="neutral">Neutros</TabsTrigger>
              <TabsTrigger value="support">Complementarios</TabsTrigger>
              <TabsTrigger value="semantic">Semánticos</TabsTrigger>
            </TabsList>
            {Object.entries(COLOR_GROUPS).map(([key, colors]) => (
              <TabsContent key={key} value={key} className="mt-5">
                <PaletteToolbar name={`via-${key}`} colors={colors} />
                <ColorGrid colors={colors} />
              </TabsContent>
            ))}
          </Tabs>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={PaintBoardIcon}
            eyebrow="03 · Temas"
            title="Temas claros, oscuros e institucionales"
            description="Descarga los tokens de cada tema en JSON o CSS y reutilízalos sin reinterpretar la identidad."
          />
          <div className="grid gap-5 lg:grid-cols-3">
            {THEMES.map((theme) => (
              <ThemeCard key={theme.id} theme={theme} />
            ))}
          </div>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={TextFontIcon}
            eyebrow="04 · Tipografía"
            title="Inter: clara, sobria y ejecutiva"
            description="Inter es la familia única para títulos, navegación, texto, datos y componentes de la interfaz VIA."
          />
          <div className="grid gap-5 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Interfaz VIA</CardTitle>
                <CardDescription>Producto digital, navegación y visualización de datos.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                <TypeSpecimen label="Inter · Bold 700 · Títulos" className="text-3xl font-bold" />
                <TypeSpecimen label="Inter · Semibold 600 · Controles" className="text-xl font-semibold" />
                <TypeSpecimen label="Inter · Regular 400 · Cuerpo" className="text-base font-normal" />
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Usa pesos 400, 500, 600 y 700. Reserva 800 para cifras ejecutivas o titulares de alto impacto.
                </p>
              </CardContent>
              <CardFooter className="flex flex-wrap gap-3">
                <Button asChild>
                  <a href="https://github.com/rsms/inter/releases/download/v4.1/Inter-4.1.zip">
                    <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                    Descargar Inter 4.1
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="https://rsms.me/inter/" target="_blank" rel="noreferrer">
                    <HugeiconsIcon icon={ExternalLinkIcon} data-icon="inline-start" />
                    Ver espécimen
                  </a>
                </Button>
              </CardFooter>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Documentos institucionales</CardTitle>
                <CardDescription>Políticas, procedimientos y entregables Office.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                <TypeSpecimen label="Calibri Light · Títulos" className="text-3xl font-light" family="Calibri" />
                <TypeSpecimen label="Calibri · Cuerpo 11 pt" className="text-lg font-normal" family="Calibri" />
                <TypeSpecimen label="Consolas · Código" className="font-mono text-base" family="Consolas" />
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Mantén Calibri en plantillas oficiales de Microsoft 365; no la uses como fuente de la aplicación.
                </p>
              </CardContent>
            </Card>
          </div>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={SparklesIcon}
            eyebrow="05 · Iconografía"
            title="Hugeicons es el único set de iconos de VIA"
            description="La aplicación usa Hugeicons Stroke Rounded para conservar una voz visual coherente, moderna y accesible."
          />
          <Card>
            <CardContent className="grid gap-8 p-6 md:grid-cols-2 md:p-8">
              <div className="flex flex-col gap-4">
                <h3 className="font-display text-lg font-bold">Patrón aprobado</h3>
                <div className="flex flex-wrap gap-4">
                  {[SecurityCheckIcon, TextFontIcon, Layout01Icon, FileImageIcon, ColorPickerIcon].map(
                    (icon, index) => (
                      <div
                        key={index}
                        className="flex size-14 items-center justify-center rounded-xl border bg-primary/5 text-primary">
                        <HugeiconsIcon icon={icon} size={24} strokeWidth={1.5} aria-hidden="true" />
                      </div>
                    ),
                  )}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Importa sólo los iconos necesarios desde <code>@hugeicons/core-free-icons</code> y renderízalos con
                  <code> HugeiconsIcon</code>. No mezcles familias en un mismo producto.
                </p>
                <Button className="w-fit" asChild>
                  <a href="https://hugeicons.com/icons" target="_blank" rel="noreferrer">
                    <HugeiconsIcon icon={GridViewIcon} data-icon="inline-start" />
                    Abrir galería de iconos
                    <HugeiconsIcon icon={ArrowUpRight01Icon} data-icon="inline-end" />
                  </a>
                </Button>
              </div>
              <div className="flex flex-col gap-3">
                {[
                  'Estilo Stroke Rounded como estándar base.',
                  'Trazo consistente de 1.5 px en interfaces.',
                  'Tamaños basados en una cuadrícula de 24 px.',
                  'El color hereda del contexto y usa tokens semánticos.',
                  'Todos los iconos interactivos incluyen nombre accesible.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <HugeiconsIcon
                      icon={CheckmarkCircle02Icon}
                      size={16}
                      className="mt-0.5 shrink-0 text-success"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={SecurityCheckIcon}
            eyebrow="06 · Gobierno de uso"
            title="Qué hacer y qué evitar"
            description="Estas reglas aplican a interfaces, presentaciones, documentos y comunicaciones vinculadas a VIA."
          />
          <div className="grid gap-5 lg:grid-cols-2">
            <UsageCard title="Uso correcto" items={CORRECT_USE} icon={CheckmarkCircle02Icon} positive />
            <UsageCard title="Uso incorrecto" items={INCORRECT_USE} icon={CancelCircleIcon} />
          </div>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={Layout01Icon}
            eyebrow="07 · Aplicaciones"
            title="Banners y piezas institucionales"
            description="Usa estas piezas en el contexto para el que fueron diseñadas y sin modificar su composición."
          />
          <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Banner corporativo</CardTitle>
                <CardDescription>Comunicaciones internas, hitos y cierres institucionales.</CardDescription>
              </CardHeader>
              <CardContent>
                <img
                  src={`${BRAND_ROOT}/banner_linkedin.png`}
                  alt="Banner La excelencia que tu salud merece"
                  className="aspect-[4/1] w-full rounded-xl border object-cover object-right"
                />
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href={`${BRAND_ROOT}/banner_linkedin.png`} download>
                    <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                    Descargar
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyAssetUrl('banner_linkedin.png', 'Banner corporativo')}>
                  <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
                  Copiar URL
                </Button>
              </CardFooter>
            </Card>
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Fondo institucional</CardTitle>
                <CardDescription>Acceso, reuniones y presentaciones.</CardDescription>
              </CardHeader>
              <CardContent>
                <img
                  src={`${BRAND_ROOT}/fondo_teams_1.png`}
                  alt="Fondo institucional UC CHRISTUS"
                  className="aspect-video w-full rounded-xl border object-cover"
                />
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href={`${BRAND_ROOT}/fondo_teams_1.png`} download>
                    <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                    Descargar
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyAssetUrl('fondo_teams_1.png', 'Fondo institucional')}>
                  <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
                  Copiar URL
                </Button>
              </CardFooter>
            </Card>
          </div>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={ExternalLinkIcon}
            eyebrow="08 · Galerías y fuentes"
            title="Recursos oficiales para diseñar y desarrollar"
            description="Accede directamente a las galerías y descargas autorizadas, sin depender de MCP."
          />
          <div className="grid gap-4 md:grid-cols-2">
            {EXTERNAL_RESOURCES.map((resource) => (
              <Card key={resource.href} className="flex h-full flex-col">
                <CardHeader className="flex-1">
                  <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <HugeiconsIcon icon={resource.icon} size={20} aria-hidden="true" />
                  </div>
                  <CardTitle className="text-base">{resource.title}</CardTitle>
                  <CardDescription>{resource.description}</CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="outline" asChild>
                    <a href={resource.href} target="_blank" rel="noreferrer">
                      {resource.action}
                      <HugeiconsIcon icon={ArrowUpRight01Icon} data-icon="inline-end" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </MotionSection>

        <MotionSection>
          <SectionHeading
            icon={Download01Icon}
            eyebrow="09 · Activos"
            title="Recursos descargables"
            description="Todos los archivos se sirven desde VIA y continuarán disponibles aunque MCP sea retirado."
          />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {DOWNLOADS.map((asset) => (
              <Card key={asset.file} className="flex h-full flex-col">
                <CardHeader className="flex-1">
                  <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <HugeiconsIcon icon={FileImageIcon} size={20} aria-hidden="true" />
                  </div>
                  <CardTitle className="text-base">{asset.label}</CardTitle>
                  <CardDescription>{asset.description}</CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`${BRAND_ROOT}/${asset.file}`} download>
                      <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
                      Descargar
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </MotionSection>
      </div>
    </div>
  );
}

function SectionHeading({ icon, eyebrow, title, description }) {
  return (
    <div className="mb-7 flex max-w-3xl items-start gap-4">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-glow">
        <HugeiconsIcon icon={icon} size={20} aria-hidden="true" />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-glow">{eyebrow}</p>
        <h2 className="mt-1 font-display text-2xl font-extrabold text-foreground sm:text-3xl">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">{description}</p>
      </div>
    </div>
  );
}

function PaletteToolbar({ name, colors }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => copyPalette(colors)}>
        <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
        Copiar paleta
      </Button>
      <Button variant="outline" size="sm" onClick={() => downloadPalette(name, colors, 'json')}>
        <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
        JSON
      </Button>
      <Button variant="outline" size="sm" onClick={() => downloadPalette(name, colors, 'css')}>
        <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
        CSS
      </Button>
    </div>
  );
}

function ColorGrid({ colors }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {colors.map((color) => (
        <Card key={`${color.hex}-${color.name}`} className="group overflow-hidden">
          <button
            type="button"
            className="relative h-24 w-full border-b text-left"
            style={{ backgroundColor: color.hex }}
            onClick={() => copyColor(color.hex, color.name)}
            aria-label={`Copiar ${color.name} ${color.hex}`}>
            <span className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-lg bg-white/90 text-slate-900 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
              <HugeiconsIcon icon={Copy01Icon} size={16} aria-hidden="true" />
            </span>
          </button>
          <CardHeader className="p-4">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">{color.name}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => copyColor(color.hex, color.name)}>
                <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
                {color.hex}
              </Button>
            </div>
            <CardDescription className="text-xs leading-relaxed">{color.usage}</CardDescription>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ThemeCard({ theme }) {
  const colors = Object.entries(theme.colors).map(([name, hex]) => ({ name, hex }));
  return (
    <Card className="overflow-hidden">
      <div className="grid h-24 grid-cols-7" aria-hidden="true">
        {colors.map((color) => (
          <div key={color.name} style={{ backgroundColor: color.hex }} />
        ))}
      </div>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={theme.icon} size={20} aria-hidden="true" />
          </div>
          <div>
            <CardTitle>{theme.name}</CardTitle>
            <CardDescription className="mt-1">{theme.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardFooter className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadTheme(theme, 'json')}>
          <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
          JSON
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadTheme(theme, 'css')}>
          <HugeiconsIcon icon={Download01Icon} data-icon="inline-start" />
          CSS
        </Button>
        <Button variant="ghost" size="sm" onClick={() => copyTheme(theme)}>
          <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
          Copiar
        </Button>
      </CardFooter>
    </Card>
  );
}

function TypeSpecimen({ label, className, family }) {
  return (
    <div className="rounded-xl border bg-muted/30 p-4">
      <p className={cn('text-foreground', className)} style={family ? { fontFamily: family } : undefined}>
        Aa Bb Cc 123 · Inteligencia con valor
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function UsageCard({ title, items, icon, positive = false }) {
  return (
    <Card className={cn(positive ? 'border-success/25' : 'border-destructive/25')}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <HugeiconsIcon icon={icon} size={20} className={positive ? 'text-success' : 'text-destructive'} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {items.map((item) => (
          <div key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
            <HugeiconsIcon
              icon={icon}
              size={16}
              className={cn('mt-0.5 shrink-0', positive ? 'text-success' : 'text-destructive')}
              aria-hidden="true"
            />
            <span>{item}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

async function copyColor(hex, name) {
  await navigator.clipboard.writeText(hex);
  toast.success(`${name}: ${hex} copiado`);
}

async function copyAssetUrl(file, label) {
  const url = new URL(`${BRAND_ROOT}/${file}`, window.location.origin).href;
  await navigator.clipboard.writeText(url);
  toast.success(`URL de ${label} copiada`);
}

async function copyPalette(colors) {
  const content = colors.map((color) => `${color.name}: ${color.hex}`).join('\n');
  await navigator.clipboard.writeText(content);
  toast.success('Paleta copiada');
}

function downloadPalette(name, colors, format) {
  const tokens = Object.fromEntries(colors.map((color) => [slugify(color.name), color.hex]));
  const content = format === 'json' ? JSON.stringify(tokens, null, 2) : toCssTokens(name, tokens);
  triggerDownload(`${name}.${format}`, content, format === 'json' ? 'application/json' : 'text/css');
}

async function copyTheme(theme) {
  await navigator.clipboard.writeText(toCssTokens(`via-${theme.id}`, theme.colors));
  toast.success(`Tema ${theme.name} copiado como variables CSS`);
}

function downloadTheme(theme, format) {
  const content = format === 'json' ? JSON.stringify(theme, null, 2) : toCssTokens(`via-${theme.id}`, theme.colors);
  triggerDownload(`via-theme-${theme.id}.${format}`, content, format === 'json' ? 'application/json' : 'text/css');
}

function toCssTokens(selector, tokens) {
  const body = Object.entries(tokens)
    .map(([key, value]) => `  --${slugify(key)}: ${value};`)
    .join('\n');
  return `.${selector} {\n${body}\n}\n`;
}

function triggerDownload(filename, content, mimeType) {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  toast.success(`${filename} descargado`);
}

function slugify(value) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
